import { useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { api, apiClient } from "../api/client";
import BolDocument from "../components/trucking/BolDocument";
import { useAuth } from "../contexts/AuthContext";
import LoadStatusBadge from "../components/trucking/LoadStatusBadge";
import { usePrintInLightTheme } from "../hooks/usePrintInLightTheme";
import { useBack } from "../hooks/useBack";

const BolDocumentView = () => {
  // A driver opens this from the BOL screen; back returns there, or home.
  const goBack = useBack("/driver/bols");
  const { loadId } = useParams<{ loadId: string }>();
  const { company } = useAuth();
  const [downloading, setDownloading] = useState(false);
  usePrintInLightTheme();

  // Fetch BOL data from API
  const { data: bolData, isLoading: bolLoading } = useQuery({
    queryKey: ['bol-detail', loadId],
    queryFn: () => apiClient.get(`/trucking/loads/bols/${loadId}`).then(r => r.data?.data ?? r.data),
    enabled: !!loadId,
  });


  /**
   * The PDF the API renders (GET /trucking/loads/bols/:id/pdf, the same file the
   * BOL email attaches), saved as a file. This button used to show a
   * "PDF downloading..." toast and download nothing (#803). A failure says so.
   */
  const handleDownloadPdf = async () => {
    if (!loadId) return;
    setDownloading(true);
    try {
      const res = await api.trucking.bols.pdf(loadId);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${bolData?.bol_number || "BOL"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't generate the PDF. Nothing was downloaded.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-full bg-background pb-[var(--bottom-chrome)] print:pb-0 print:bg-transparent">

      {/* Toolbar. Not printed. It used to read "— Signed Document" for every
        * status, cancelled included, beside three buttons that only raised a
        * toast (#803): "Email queued to AP" sent nothing and "Create invoice"
        * opened nothing, so those two are gone — the real invoice action is the
        * Generate Invoice button below. The status is the shared badge. */}
      <div data-testid="bol-toolbar" className="border-b border-border print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* The way back, now that no bottom bar is drawn under this screen. */}
            <button type="button" onClick={goBack} aria-label="Back" className="grid h-9 w-9 place-items-center rounded-lg border border-border">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              BOL #{bolData ? (bolData.bol_number || "—") : "…"}
            </p>
            {bolData && <LoadStatusBadge status={bolData.status} />}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={() => window.print()} disabled={!bolData}>
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
            <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={handleDownloadPdf} disabled={!bolData || downloading}>
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-5xl mx-auto px-4 py-6 print:p-0">
        {bolLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : bolData ? (
          <BolDocument bol={bolData} companyName={company?.name} />
        ) : (
          <div className="text-center py-20 text-muted-foreground">BOL not found</div>
        )}
      </div>

      {/* No invoice section (#815). It looked up "this BOL's invoice" with a
          `bol_id` filter GET /trucking/invoices ignores, so it showed the
          tenant's newest invoice whatever the BOL; Generate and View both went
          to /trucking/invoices/:id, which is not a page; and a driver (this is
          the driver's BOL screen) is refused both routes. A trucking invoice
          screen is its own ticket. */}
    </div>
  );
};

export default BolDocumentView;
